import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  PdsMantineButton,
  PdsExternalLinkIcon,
} from '@podium-design-system/react-components';

function readLinkField(link, camel, snake) {
  const v = link?.[camel] ?? link?.[snake];
  return v == null || v === '' ? '' : String(v);
}

/**
 * One remote case–node linkage: Case target, node target, and navigation.
 * Fetches investigation /details when the API row omits otherInvestigationTarget.
 */
export default function CrossCaseLinkageRecord({
  link,
  onNavigate,
  /** When true, use Podium button (CaseDetails modal); otherwise compact link (network graph modal). */
  podium = false,
  style,
}) {
  const caseTarget = readLinkField(link, 'otherCaseTarget', 'other_case_target') || '—';
  const caseId = readLinkField(link, 'otherCaseId', 'other_case_id');
  const investigationId = readLinkField(link, 'otherInvestigationId', 'other_investigation_id');
  const targetFromLink = readLinkField(link, 'otherInvestigationTarget', 'other_investigation_target');

  const [nodeTarget, setNodeTarget] = useState(targetFromLink);

  useEffect(() => {
    if (targetFromLink) {
      setNodeTarget(targetFromLink);
      return undefined;
    }
    if (!investigationId) {
      setNodeTarget('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/investigations/${investigationId}/details`);
        if (!cancelled) {
          setNodeTarget(res.data?.targetValue ? String(res.data.targetValue) : '');
        }
      } catch (_) {
        if (!cancelled) setNodeTarget('');
      }
    })();
    return () => { cancelled = true; };
  }, [investigationId, targetFromLink]);

  const nodeDisplay = nodeTarget || (investigationId ? '…' : '—');
  const canNavigate = Boolean(caseId && investigationId);

  const handleNavigate = () => {
    onNavigate?.();
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 12,
        background: '#f8fafc',
        ...style,
      }}
    >
      <div style={{ fontSize: 13, color: '#334155', marginBottom: 4 }}>
        <strong>Case:</strong>{' '}
        <span className="argus-data">{caseTarget}</span>
      </div>
      <div style={{ fontSize: 13, color: '#334155', marginBottom: canNavigate ? 10 : 0 }}>
        <strong>Node:</strong>{' '}
        <span className="argus-data">{nodeDisplay}</span>
      </div>
      {canNavigate && (
        podium ? (
          <PdsMantineButton
            component={Link}
            to={`/cases/${caseId}`}
            state={{ selectedInvestigationId: investigationId }}
            rank="fill"
            color="action"
            size="sm"
            onClick={handleNavigate}
            leadingIcon={<PdsExternalLinkIcon />}
          >
            View node
          </PdsMantineButton>
        ) : (
          <Link
            to={`/cases/${caseId}`}
            state={{ selectedInvestigationId: investigationId }}
            className="btn btn-sm btn-secondary"
            style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap', display: 'inline-block' }}
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
          >
            View node
          </Link>
        )
      )}
    </div>
  );
}
